// Medals: one for the Level 1 Support Desk and one per learning domain (D01–D07), each tied to its
// city on the Mission Map. A medal is DERIVED from verified completion records — never stored or
// synced as a medal, never awarded for admin bypass / test records, and it unlocks nothing.
// The only stored reward state is `awards-seen` (learner store): which medal pop-ups were already
// shown, so a pop-up appears once. It is presentation only.
import { SUPPORT_TICKETS } from '../domains/support/tickets.js';
import { CITIES } from '../map/mapData.js';
import { operatorStats } from '../map/mapState.js';

export const SEEN_KEY = 'awards-seen';

/** id · medal name · what it is for · icon key (src/ui/medals-view.js) */
const DOMAIN_MEDALS = {
  d01: { icon: 'nodes', title: 'Packet Pathfinder' }, d02: { icon: 'switch', title: 'VLAN Wrangler' }, d03: { icon: 'router', title: 'Route Ranger' }, d04: { icon: 'globe', title: 'WAN Voyager' },
  d05: { icon: 'gear', title: 'Service Keeper' }, d06: { icon: 'shield', title: 'Access Guardian' }, d07: { icon: 'clipboard', title: 'Change Commander' },
};

/** The medal list, in path order. */
export function medalList(curriculum) {
  const list = [{ id: 'support', kind: 'support', name: 'Support Desk Medal', title: 'Level 1 Support Desk', place: 'RouteZero Manila HQ', icon: 'headset', goal: 'Complete all five Level 1 Support Desk tickets', unlockTitle: 'Help Desk Hero' }];
  for (const d of curriculum.domains) {
    if (!DOMAIN_MEDALS[d.id]) continue;
    const city = CITIES.find((c) => c.domainId === d.id);
    list.push({ id: d.id, kind: 'domain', domainId: d.id, number: d.number, name: `${city ? city.name : d.title} Medal`, title: `Domain ${d.number} · ${d.title}`, place: city?.name || '', icon: DOMAIN_MEDALS[d.id].icon, goal: `Complete every mission in Domain ${d.number}`, unlockTitle: DOMAIN_MEDALS[d.id].title });
  }
  return list;
}

const verified = (r) => r && r.status === 'complete' && !r.test && !r.bypass && r.validated !== false;

/**
 * Every medal with `earned` and (when known) `earnedAt`.
 * @returns {Array<{id:string,name:string,title:string,earned:boolean,earnedAt:number|null}>}
 */
export function medalStatus({ progress, curriculum, support }) {
  return medalList(curriculum).map((m) => {
    if (m.kind === 'support') {
      const recs = support ? SUPPORT_TICKETS.map((t) => support.ticketRecord(t.id)) : [];
      const earned = recs.length > 0 && recs.every(verified);
      const at = earned ? Math.max(...recs.map((r) => Number(r.completedAt) || 0)) : 0;
      return { ...m, earned, earnedAt: at || null };
    }
    const d = curriculum.domains.find((x) => x.id === m.domainId);
    const implemented = d.units.filter((u) => u.implemented);
    const earned = implemented.length > 0 && progress.isDomainComplete(d.id)
      && implemented.every((u) => verified({ status: 'complete', ...(progress.unitRecord?.(d.id, u.id) || {}) }));
    const at = earned ? Number(progress.raw?.().domains?.[d.id]?.completedAt) || null : null;
    return { ...m, earned, earnedAt: at };
  });
}

/** Total XP and level exactly as the navigation shows them (missions via xp.js + 100 per ticket). */
export function learnerXp({ progress, curriculum, support }) {
  const xp = operatorStats(progress, curriculum).xp + (support?.summary?.().done || 0) * 100;
  return { xp, level: 1 + Math.floor(xp / 500) };
}

/** What changed between two snapshots: XP gained, a new level, newly earned medal ids. */
export function rewardDiff(before, after) {
  if (!before || !after) return { xp: 0, levelUp: null, medals: [] };
  return {
    xp: Math.max(0, after.xp - before.xp),
    levelUp: after.level > before.level ? after.level : null,
    medals: after.medals.filter((id) => !before.medals.includes(id)),
  };
}

// ---------------------------------------------------------------- titles and avatar frames
// Cosmetic rewards unlocked by medals (derived; the learner's choice is stored under `look`).
export const LOOK_KEY = 'look';
export const DEFAULT_TITLE = 'RouteZero Learner';
/** Frames unlock by number of medals earned. */
export const FRAMES = [
  { id: 'none', name: 'No frame', need: 0 },
  { id: 'cyan', name: 'Cyan ring', need: 1 },
  { id: 'violet', name: 'Violet ring', need: 3 },
  { id: 'gradient', name: 'Purple → cyan', need: 5 },
  { id: 'aurora', name: 'Aurora (animated)', need: 8 },
];
/** Titles and frames this learner can choose, plus the valid current choice. */
export function lookOptions(status, saved = {}) {
  const earned = status.filter((m) => m.earned);
  const titles = [DEFAULT_TITLE, ...earned.map((m) => m.unlockTitle).filter(Boolean)];
  const frames = FRAMES.map((f) => ({ ...f, unlocked: earned.length >= f.need }));
  const pick = saved && typeof saved === 'object' ? saved : {};
  const title = titles.includes(pick.title) ? pick.title : (titles.at(-1) || DEFAULT_TITLE);
  const best = [...frames].reverse().find((f) => f.unlocked)?.id || 'none';
  const frame = frames.some((f) => f.id === pick.frame && f.unlocked) ? pick.frame : best;
  return { titles, frames, title, frame, earnedCount: earned.length };
}
